/**
 * Bundle Cart Sync (duplicate-merge only)
 * ----------------
 * Include this on every page (theme.liquid, right before </body>).
 *
 * The only behavior left here: if the main product is added to the cart
 * twice — once through the bundle widget (tagged with _bundle_role) and
 * once through the theme's own default Add to Cart button (untagged) —
 * in either order, the two lines are merged into one instead of showing
 * as two separate rows.
 *
 * There is NO removal cascade and NO quantity syncing between bundle
 * items — removing or changing the quantity of any single line has no
 * effect on any other line.
 */
(function () {
  const REFRESH_ENDPOINTS = ['/cart/add.js'];

  let merging = false;

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  function mainItems(cart) {
    return cart.items.filter((item) => item.properties && item.properties._bundle_role === 'main');
  }

  async function updateLineQuantity(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
  }

  function notifyCartUI(cart) {
    if (typeof window.updateMiniCartSection === 'function') {
      window.updateMiniCartSection();
    }
    if (typeof window.miniCartInit === 'function') {
      window.miniCartInit();
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { cart } }));
  }

  async function mergeDuplicateMainLines() {
    const cart = await fetchCart();
    const mainLines = mainItems(cart);
    let merged = false;

    for (const mainLine of mainLines) {
      const duplicates = cart.items.filter(
        (item) =>
          item.key !== mainLine.key &&
          String(item.variant_id) === String(mainLine.variant_id) &&
          (!item.properties || !item.properties._bundle_role)
      );

      for (const dup of duplicates) {
        await updateLineQuantity(mainLine.key, mainLine.quantity + dup.quantity);
        await updateLineQuantity(dup.key, 0);
        merged = true;
      }
    }

    if (!merged) return null;
    const finalCart = await fetchCart();
    notifyCartUI(finalCart);
    return finalCart;
  }

  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);

    try {
      const requestUrl = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;

      if (requestUrl && REFRESH_ENDPOINTS.some((endpoint) => requestUrl.includes(endpoint)) && !merging) {
        merging = true;
        mergeDuplicateMainLines()
          .catch(() => {})
          .finally(() => {
            merging = false;
          });
      }
    } catch (e) {
      // Never let this logic break the theme's own cart requests.
    }

    return response;
  };
})(); 